/**
 * Geographic Intelligence Page
 * 
 * Choropleth map with zoom-adaptive granularity:
 * - Zoomed out: state-level colored regions
 * - Zoomed in: zip-level density dots
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapPin, Search, Shield, AlertTriangle } from 'lucide-react';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Popup, ZoomControl, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getZipCentroid } from '../data/zipCentroids';
import {
  getCarrierDensity,
  checkAddress,
  getSafetyStats,
} from '../api/geographic';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import statesGeoJSON from '../data/us-states.json';
import '../styles/modern.css';

// ============================================================
// TYPES
// ============================================================

interface DensityItem {
  region: string;
  state?: string;
  city?: string;
  carrier_count: string | number;
  active_carriers?: string | number;
}

// ============================================================
// CONSTANTS
// ============================================================

const US_STATES_LIST = [
  { value: '', label: 'All States' },
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

// State name -> abbreviation mapping (GeoJSON uses full names)
const STATE_NAME_TO_ABBR: Record<string, string> = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
  'District of Columbia': 'DC', 'Puerto Rico': 'PR',
};

type TabType = 'map' | 'address' | 'safety';

// ============================================================
// COLOR UTILITIES
// ============================================================

function getDensityColor(count: number, max: number): string {
  if (max === 0) return '#f5f2ea';
  const ratio = count / max;
  if (ratio > 0.6) return '#963524';  // dark vermilion
  if (ratio > 0.4) return '#b3412c';  // vermilion
  if (ratio > 0.25) return '#7d561c'; // dark ochre
  if (ratio > 0.15) return '#b07d2b'; // ochre
  if (ratio > 0.08) return '#5d7148'; // moss
  if (ratio > 0.03) return '#e2e7d5'; // moss tint
  return '#e2e7d5'; // moss tint
}

// ============================================================
// MAIN COMPONENT
// ============================================================

const GeographicIntelligence: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('map');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Geographic Intelligence</h1>
          <p className="text-gray-600">Analyze carrier density, address clustering, and regional safety patterns</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {([
                { id: 'map' as TabType, label: 'Carrier Map', icon: MapPin },
                { id: 'address' as TabType, label: 'Address Check', icon: Search },
                { id: 'safety' as TabType, label: 'Safety Analytics', icon: Shield },
              ]).map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}>
                  <tab.icon className="w-4 h-4" />{tab.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="p-6">
            {activeTab === 'map' && <CarrierMapTab />}
            {activeTab === 'address' && <AddressCheckTab />}
            {activeTab === 'safety' && <SafetyAnalyticsTab />}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// TAB 1: CARRIER MAP — Choropleth with zoom-adaptive layers
// ============================================================

// Component that handles zoom events and switches layers
const ZoomHandler: React.FC<{ onZoomChange: (zoom: number) => void }> = ({ onZoomChange }) => {
  useMapEvents({
    zoomend: (e) => { onZoomChange(e.target.getZoom()); },
  });
  return null;
};

const CarrierMapTab: React.FC = () => {
  const [stateData, setStateData] = useState<DensityItem[]>([]);
  const [zipData, setZipData] = useState<DensityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(4);
  const [selectedState, setSelectedState] = useState('');
  const geoJsonRef = useRef<L.GeoJSON | null>(null);

  // Load state data on mount, zip data when zoomed in
  useEffect(() => {
    loadStateData();
  }, []);

  useEffect(() => {
    if (zoom >= 6 || selectedState) {
      loadZipData();
    }
  }, [zoom, selectedState]);

  const loadStateData = async () => {
    try {
      setLoading(true);
      const data = await getCarrierDensity('state');
      setStateData(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadZipData = async () => {
    try {
      const data = await getCarrierDensity('zip', selectedState || undefined, 5);
      setZipData(Array.isArray(data) ? data : []);
    } catch (err) { /* silent */ }
  };

  // Build state density lookup
  const stateDensity = useMemo(() => {
    const map: Record<string, number> = {};
    stateData.forEach(d => { map[d.region] = Number(d.carrier_count) || 0; });
    return map;
  }, [stateData]);

  const maxStateCount = useMemo(() => Math.max(...Object.values(stateDensity), 1), [stateDensity]);

  // GeoJSON style function for state choropleth
  const stateStyle = useCallback((feature: any) => {
    const abbr = STATE_NAME_TO_ABBR[feature?.properties?.name] || '';
    const count = stateDensity[abbr] || 0;
    return {
      fillColor: getDensityColor(count, maxStateCount),
      weight: 1.5,
      opacity: 1,
      color: '#fbf9f4',
      fillOpacity: 0.7,
    };
  }, [stateDensity, maxStateCount]);

  // GeoJSON popup on each state
  const onEachState = useCallback((feature: any, layer: L.Layer) => {
    const name = feature?.properties?.name || 'Unknown';
    const abbr = STATE_NAME_TO_ABBR[name] || '';
    const count = stateDensity[abbr] || 0;
    const stateInfo = stateData.find(d => d.region === abbr);
    const active = stateInfo ? Number(stateInfo.active_carriers) || 0 : 0;

    layer.bindPopup(`
      <div style="padding:4px;min-width:160px">
        <div style="font-weight:bold;font-size:14px;margin-bottom:4px">${name} (${abbr})</div>
        <div style="font-size:13px"><strong>${count.toLocaleString()}</strong> carriers</div>
        <div style="font-size:12px;color:#6d685e">${active.toLocaleString()} active</div>
      </div>
    `);

    (layer as any).on({
      mouseover: (e: any) => {
        e.target.setStyle({ weight: 3, color: '#23211d', fillOpacity: 0.85 });
      },
      mouseout: (e: any) => {
        if (geoJsonRef.current) geoJsonRef.current.resetStyle(e.target);
      },
      click: (e: any) => {
        setSelectedState(abbr);
      }
    });
  }, [stateDensity, stateData]);

  // Zip-level dots (shown when zoomed in)
  const zipMarkers = useMemo(() => {
    if (zoom < 6 && !selectedState) return [];
    return zipData
      .map(item => {
        const centroid = getZipCentroid(item.region);
        if (!centroid) return null;
        return { ...item, lat: centroid.lat, lng: centroid.lng, count: Number(item.carrier_count) || 0 };
      })
      .filter(Boolean) as Array<DensityItem & { lat: number; lng: number; count: number }>;
  }, [zipData, zoom, selectedState]);

  const maxZipCount = useMemo(() => Math.max(...zipMarkers.map(z => z.count), 1), [zipMarkers]);

  const showZipLayer = zoom >= 6 || !!selectedState;

  return (
    <div>
      {/* Controls */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Focus State</label>
          <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
            {US_STATES_LIST.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        {selectedState && (
          <button onClick={() => setSelectedState('')}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Clear Filter
          </button>
        )}
        <div className="text-sm text-gray-500 ml-auto">
          Zoom: {zoom} — {showZipLayer ? 'Showing ZIP-level detail' : 'Showing state overview (zoom in for ZIP detail)'}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[600px]"><LoadingSpinner size="lg" /></div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      ) : (
        <>
          <div style={{ width: '100%', height: '600px', borderRadius: 'var(--radius-md)', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <MapContainer
              center={[39.8, -98.5]}
              zoom={4}
              zoomControl={false}
              scrollWheelZoom={true}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'
              />
              {/* Labels on top */}
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
                pane="tooltipPane"
              />
              <ZoomControl position="bottomright" />
              <ZoomHandler onZoomChange={setZoom} />

              {/* State choropleth layer */}
              {stateData.length > 0 && (
                <GeoJSON
                  ref={(ref) => { geoJsonRef.current = ref as any; }}
                  data={statesGeoJSON as any}
                  style={stateStyle}
                  onEachFeature={onEachState}
                  key={JSON.stringify(stateDensity)}
                />
              )}

              {/* ZIP-level detail when zoomed in */}
              {showZipLayer && zipMarkers.map((item, idx) => (
                <CircleMarker
                  key={idx}
                  center={[item.lat, item.lng]}
                  radius={Math.min(Math.max(Math.sqrt(item.count) * 1.2, 3), 20)}
                  fillColor={getDensityColor(item.count, maxZipCount)}
                  fillOpacity={0.75}
                  stroke={true}
                  color="rgba(255,255,255,0.6)"
                  weight={1}
                >
                  <Popup>
                    <div style={{ padding: '4px' }}>
                      <div style={{ fontWeight: 'bold' }}>ZIP {item.region}</div>
                      {item.city && <div style={{ fontSize: '12px', color: 'var(--color-gray-600)' }}>{item.city}, {item.state}</div>}
                      <div style={{ fontSize: '13px', marginTop: '2px' }}><strong>{item.count.toLocaleString()}</strong> carriers</div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>

          {/* Legend */}
          <div className="mt-4 bg-white rounded-lg p-4 border border-gray-200">
            <div className="font-medium text-gray-900 mb-2">Carrier Density</div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 mr-1">Low</span>
              {['#f5f2ea', '#e2e7d5', '#5d7148', '#b07d2b', '#7d561c', '#b3412c', '#963524'].map((c, i) => (
                <div key={i} style={{ width: 32, height: 16, backgroundColor: c, borderRadius: i === 0 ? '4px 0 0 4px' : i === 6 ? '0 4px 4px 0' : '0' }} />
              ))}
              <span className="text-xs text-gray-500 ml-1">High</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================
// TAB 2: ADDRESS CHECK
// ============================================================
const AddressCheckTab: React.FC = () => {
  const [formData, setFormData] = useState({ address: '', city: '', state: '', zip: '' });
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.zip && !(formData.city && formData.state)) {
      setError('Enter a ZIP code or city + state');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const raw = await checkAddress(formData);
      const exactCarriers = raw?.exactMatches?.carriers || [];
      const nearbyCarriers = raw?.nearbyMatches?.carriers || [];
      setResults({ exactMatches: exactCarriers, nearbyCarriers, totalAtAddress: exactCarriers.length, totalInZip: nearbyCarriers.length });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check address');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
            <input type="text" value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="123 Main St" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
            <input type="text" value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="City" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
            <select value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">Select State</option>
              {US_STATES_LIST.slice(1).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
            <input type="text" value={formData.zip}
              onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="12345" maxLength={5} />
          </div>
        </div>
        <button type="submit" disabled={loading}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400">
          <Search className="w-4 h-4" />
          {loading ? 'Searching...' : 'Check Address'}
        </button>
      </form>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-6">{error}</div>}

      {results && (
        <div className="space-y-6">
          {results.totalAtAddress >= 5 && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-bold text-red-900 mb-1">⚠️ Potential Chameleon Cluster</h3>
                  <p className="text-red-700">
                    <strong>{results.totalAtAddress}</strong> carriers share this exact address — possible shell companies or fraud.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Exact Address Matches ({results.exactMatches.length})</h3>
            {results.exactMatches.length > 0 ? (
              <div className="space-y-3">{results.exactMatches.map((c: any) => <CarrierCard key={c.dot_number} carrier={c} highlighted />)}</div>
            ) : <div className="text-gray-500 italic">No carriers at this exact address</div>}
          </div>

          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Same ZIP Code ({results.nearbyCarriers.length})</h3>
            {results.nearbyCarriers.length > 0 ? (
              <div className="space-y-3">
                {results.nearbyCarriers.slice(0, 20).map((c: any) => <CarrierCard key={c.dot_number} carrier={c} />)}
                {results.nearbyCarriers.length > 20 && (
                  <div className="text-sm text-gray-500 italic text-center">+ {results.nearbyCarriers.length - 20} more</div>
                )}
              </div>
            ) : <div className="text-gray-500 italic">No other carriers in this ZIP</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const CarrierCard: React.FC<{ carrier: any; highlighted?: boolean }> = ({ carrier, highlighted }) => (
  <div className={`border rounded-lg p-4 ${highlighted ? 'bg-yellow-50 border-yellow-300' : 'bg-white border-gray-200'}`}>
    <div className="flex items-start justify-between mb-2">
      <div>
        <h4 className="font-bold text-gray-900">{carrier.legal_name}</h4>
        {carrier.dba_name && carrier.dba_name !== carrier.legal_name && (
          <div className="text-sm text-gray-600">DBA: {carrier.dba_name}</div>
        )}
      </div>
      <div className="text-right">
        <div className="text-sm font-medium text-gray-900">DOT# {carrier.dot_number}</div>
        <div className={`text-xs px-2 py-1 rounded inline-block mt-1 ${
          carrier.status_code === 'A' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>{carrier.status_code === 'A' ? 'Active' : 'Inactive'}</div>
      </div>
    </div>
    <div className="text-sm text-gray-600 mb-3">
      <div>{carrier.phy_street}</div>
      <div>{carrier.phy_city}, {carrier.phy_state} {carrier.phy_zip}</div>
    </div>
    <div className="flex items-center gap-4 text-sm">
      <div><span className="text-gray-500">Op:</span> <span className="font-medium">{carrier.carrier_operation}</span></div>
      <div><span className="text-gray-500">Drivers:</span> <span className="font-medium">{carrier.total_drivers}</span></div>
      <div><span className="text-gray-500">Trucks:</span> <span className="font-medium">{carrier.power_units}</span></div>
    </div>
  </div>
);

// ============================================================
// TAB 3: SAFETY ANALYTICS
// ============================================================
const SafetyAnalyticsTab: React.FC = () => {
  const [safetyData, setSafetyData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('carrier_count');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await getSafetyStats('state');
        setSafetyData(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load safety data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sortedData = useMemo(() => {
    return [...safetyData].sort((a, b) => {
      const av = sortField === 'region' ? a[sortField] : (Number(a[sortField]) || 0);
      const bv = sortField === 'region' ? b[sortField] : (Number(b[sortField]) || 0);
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv as string) : (bv as string).localeCompare(av);
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [safetyData, sortField, sortDir]);

  const columns = [
    { key: 'region', label: 'State' },
    { key: 'carrier_count', label: 'Carriers' },
    { key: 'total_inspections', label: 'Inspections' },
    { key: 'total_crashes', label: 'Crashes' },
    { key: 'avg_oos_rate', label: 'OOS Rate' },
    { key: 'safety_score', label: 'Risk Score' },
  ];

  return (
    <div>
      {loading ? (
        <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map(col => (
                    <th key={col.key} onClick={() => handleSort(col.key)}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                      {col.label} {sortField === col.key && (sortDir === 'asc' ? '↑' : '↓')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedData.map((item, idx) => {
                  const oosRate = Number(item.avg_oos_rate) || 0;
                  const risk = Number(item.safety_score) || 0;
                  const riskColor = risk > 4 ? 'text-red-700 bg-red-50' :
                    risk > 3 ? 'text-orange-700 bg-orange-50' :
                    risk > 2 ? 'text-yellow-700 bg-yellow-50' : 'text-green-700 bg-green-50';

                  return (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.region}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{Number(item.carrier_count).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{Number(item.total_inspections).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{Number(item.total_crashes).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{(oosRate * 100).toFixed(1)}%</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${riskColor}`}>{risk.toFixed(2)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeographicIntelligence;
